import { useInstance } from '@meraki-internal/react-dependency-injection';
import { ProState } from './ProState';
import { useSubscription } from '@meraki-internal/state';
import { IonCard, IonModal, IonText } from '@ionic/react';
import { Button } from '../components/button/Button';
import { CheckmarkIcon, CloseIcon } from '../components/icons/Icons';
import { PageContent } from '../components/page/PageContent';
import { PageHeader } from '../components/page/PageHeader';
import { PageFooter } from '../components/page/PageFooter';
import { OperatorFlagsState } from '../profile/OperatorFlagsState';

const TableRow: React.FC<{ description?: string, free?: any, plus?: any, noBorder?: boolean }> = ({ description = '', free, plus, noBorder }) => {
    const borderBottom = noBorder ? undefined : '1px solid #c0c0c0';
    const backgroundColor = 'rgba(var(--ion-color-primary-rgb), 0.25)';
    return (
        <tr style={{ fontSize: 14, fontWeight: 'bold' }}>
            <td style={{ borderBottom, paddingRight: 20 }}>
                {description}
            </td>
            <td style={{ width: 80, padding: '20px 0', textAlign: 'center', borderBottom }}>
                {free ? <small>{free}</small> : <CloseIcon size="medium" color="medium" />}
            </td>
            <td style={{ width: 90, padding: '20px 0', textAlign: 'center', borderBottom, backgroundColor }}>
                {plus || <CheckmarkIcon size="medium" color="primary" strokeWidth={60} />}
            </td>
        </tr>
    );
};

export const PaywallModal: React.FC = () => {
    const proState = useInstance(ProState);
    useSubscription(() => proState);

    const flags = useInstance(OperatorFlagsState);
    useSubscription(() => flags);

    const upgrade = async () => {
        await proState.upgrade();
    };

    const paywallScreen = proState.hasPro() ? 'confirmation' : 'paywall';

    let pricePerMonth = '$6.99';
    let pricePerDay = '$0.25';

    if (flags.hasFlag('weather-price-399')){
        pricePerMonth = '$3.99';
        pricePerDay = '$0.15';
    }

    return (
        <IonModal isOpen={proState.isShowingPaywall}>

            {paywallScreen === 'paywall' && (
                <>
                    <PageHeader transparent onBack={() => proState.closePaywall()} />

                    <PageContent noScroll>
                        <div style={{ padding: '0 30px' }}>
                            <h1>
                                <b>Find the best time to fly with AutoPylot<IonText color="primary">+</IonText></b>
                            </h1>

                            <IonCard style={{ margin: '30px 0' }}>
                                <div style={{ marginLeft: 20 }}>
                                    <table style={{ width: '100%' }}>
                                        <TableRow
                                            free={<IonText color="medium" style={{ fontSize: 14 }}>Free</IonText>}
                                            plus={<span style={{ color: 'var(--ion-text-color)' }}>AutoPylot<IonText color="primary">+</IonText></span>}
                                            noBorder
                                        />
                                        <TableRow description="Unlimited flight planning weather" free="Limited" />
                                        <TableRow description="Adjust flight plan from weather" />
                                        {flags.hasFlag('weather-paywall-on-summary') && (
                                            <>
                                                <TableRow description="24-hour flight forecasts" />
                                                <TableRow description="10-day flight forecasts" />
                                            </>
                                        )}
                                        {!flags.hasFlag('weather-paywall-on-summary') && (
                                            <>
                                                <TableRow description="24-hour flight forecasts" free="2 hours" />
                                                <TableRow description="10-day flight forecasts" free="2 days" />
                                            </>
                                        )}

                                        <TableRow description="New AutoPylot+ features" noBorder />
                                    </table>
                                </div>
                            </IonCard>
                        </div>
                    </PageContent>

                    <PageFooter>
                        <hr style={{ backgroundColor: '#eee' }} />

                        <div style={{ padding: '5px 30px 30px' }}>
                            <div style={{ lineHeight: 0.8, marginBottom: 15 }}>
                                <IonText color="medium">
                                    <small>
                                        By tapping Continue, you will be charged, your subscription will auto-renew
                                        for the same price and duration until you cancel via Store settings,
                                        and you agree to our <a href="https://www.autopylot.io/terms/">Terms</a>.
                                    </small>
                                </IonText>
                            </div>

                            <Button expand="block" onClick={upgrade}>Continue - {pricePerMonth} / month</Button>

                            <div style={{ width: '100%', textAlign: 'center', marginTop: 10 }}>
                                <IonText color="medium">Less than {pricePerDay} / day! Cancel any time.</IonText>
                            </div>
                        </div>
                    </PageFooter>
                </>
            )}

            {paywallScreen === 'confirmation' && (
                <PageContent noScroll>

                    <div style={{ margin: 30 }}>

                        <div style={{ margin: '60px 0 40px' }}>
                            <h1><b>First month's on us!</b></h1>
                            <p>
                                Great news! Although you opted to pay for a subscription now, you won't be charged.
                                For the next 30 days, enjoy full access to AutoPylot+ features on us.
                                Thank you for your support!
                            </p>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 30, minWidth: 330, overflow: 'hidden' }}>
                            <img style={{ height: 190 }} src="./assets/images/paywall/timeline.svg" alt="Timeline" />

                            <div>
                                <div style={{ height: 80 }}>
                                    <h4 style={{ margin: 0 }}><b>Today:</b> Get Instant Access</h4>
                                    <p style={{ margin: 0 }}>Unlimited flight planning weather, no credit card required</p>
                                </div>

                                <div style={{ height: 80 }}>
                                    <h4 style={{ margin: 0 }}><b>Day 28:</b> AutoPylot+ Reminder</h4>
                                    <p style={{ margin: 0 }}>We'll send you a notification reminding you your access ends soon</p>
                                </div>

                                <div style={{ height: 80 }}>
                                    <h4 style={{ margin: 0 }}><b>Day 30:</b> Free Access Ends</h4>
                                    <p style={{ margin: 0 }}>You'll need to pay to continue your access to AutoPylot+</p>
                                </div>
                            </div>
                        </div>

                        <Button expand="block" onClick={() => proState.closePaywall()}>Continue</Button>

                    </div>

                </PageContent>
            )}

        </IonModal>
    );

};
